import { apiFetch } from './api';
import { useAuthStore } from '../store/useAuthStore';

// --- Types (mirror backend src/modules/calendar) --------------------------

export type CalendarEventStatus = 'CONFIRMED' | 'TENTATIVE' | 'CANCELLED';

export interface CalendarEvent {
  id: string;
  googleEventId: string | null;
  calendarId: string;
  calendarName: string;
  summary: string;
  description: string | null;
  location: string | null;
  startTime: string;
  endTime: string;
  allDay: boolean;
  status: CalendarEventStatus;
  // Hex of the colour Google Calendar paints this event with (its own, else
  // its calendar's), or null when unknown.
  color: string | null;
}

export interface EventConflict {
  eventId: string;
  conflictsWith: string;
  summary: string;
  conflictSummary: string;
  overlapStart: string;
  overlapEnd: string;
}

export interface CalendarEventsResponse {
  events: CalendarEvent[];
  totalEvents: number;
  conflicts: EventConflict[];
  lastSyncAt: string | null;
}

export interface SyncStats {
  eventsCreated: number;
  eventsUpdated: number;
  eventsDeleted: number;
  lastSyncAt: string;
}

interface Envelope<T> {
  success: true;
  data: T;
}

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export interface CalendarEventsFilters {
  userId?: string;
  date?: string; // YYYY-MM-DD
  startDate?: string; // ISO
  endDate?: string; // ISO
  refresh?: boolean;
  // Drop the Google copies of Firmio Meet meetings (host copy + emailed .ics)
  // for screens that already show those meetings from the Meet module.
  excludeMeet?: boolean;
}

export async function listCalendarEvents(
  filters: CalendarEventsFilters = {},
): Promise<CalendarEventsResponse> {
  const params = new URLSearchParams();
  if (filters.userId) params.set('userId', filters.userId);
  if (filters.date) params.set('date', filters.date);
  if (filters.startDate) params.set('startDate', filters.startDate);
  if (filters.endDate) params.set('endDate', filters.endDate);
  if (filters.refresh) params.set('refresh', 'true');
  if (filters.excludeMeet) params.set('excludeMeet', 'true');
  const qs = params.toString();
  const res = await apiFetch<Envelope<CalendarEventsResponse>>(
    `/api/calendar/events${qs ? `?${qs}` : ''}`,
    { headers: authHeaders() },
  );
  return res.data;
}

export async function syncCalendar(input: {
  userId?: string;
  startDate?: string;
  endDate?: string;
}): Promise<SyncStats> {
  const res = await apiFetch<Envelope<SyncStats>>('/api/calendar/sync', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  return res.data;
}

export async function getCalendarAuthUrl(state?: string): Promise<{ authUrl: string }> {
  const res = await apiFetch<Envelope<{ authUrl: string }>>('/api/calendar/auth', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ action: 'url', state }),
  });
  return res.data;
}

export interface CreateCalendarEventInput {
  userId?: string;
  title: string;
  description?: string;
  location?: string;
  startTime: string;
  endTime: string;
  allDay?: boolean;
  status?: CalendarEventStatus;
  calendarId?: string;
}

export async function createCalendarEvent(
  input: CreateCalendarEventInput,
): Promise<{ event: CalendarEvent; conflicts: EventConflict[] }> {
  const res = await apiFetch<Envelope<{ event: CalendarEvent; conflicts: EventConflict[] }>>(
    '/api/calendar/events',
    { method: 'POST', headers: authHeaders(), body: JSON.stringify(input) },
  );
  return res.data;
}

export async function updateCalendarEvent(
  id: string,
  input: Partial<Omit<CreateCalendarEventInput, 'userId'>>,
): Promise<{ event: CalendarEvent; conflicts: EventConflict[] }> {
  const res = await apiFetch<Envelope<{ event: CalendarEvent; conflicts: EventConflict[] }>>(
    `/api/calendar/events/${id}`,
    { method: 'PUT', headers: authHeaders(), body: JSON.stringify(input) },
  );
  return res.data;
}
